import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import LiveDot from '../../../atoms/LiveDot';
import {
  PencilIcon,
  ShareIcon,
  EyeIcon,
  ArrowRightIcon,
  LocationIcon,
  StyleIcon,
} from '../../../atoms/icons/EditorialProfileIcons';
import { ReactComponent as LyreIcon } from '../../../../images/lyre-icon.svg';
import HeroCoverEditButton from '../../../atoms/HeroCoverEditButton';
import classNames from '../../../../utils/classNames';
import EditableText from '../../../molecules/EditableText';
import EditorialHeroStatsRail from '../../../molecules/EditorialHeroStatsRail';
import StyleTagsEditor from '../../../molecules/StyleTagsEditor';
import PostVisibilityControl from '../../../molecules/PostVisibilityControl';
import { PROFILE_VISIBILITY_OPTIONS } from '../../../molecules/PostVisibilityControl/options';
import {
  PROFILE_DISPLAY_NAME_MAX_LENGTH,
  PROFILE_PREFERRED_STYLE_EMPTY_LABEL,
  PROFILE_PREFERRED_STYLE_INPUT_PLACEHOLDER,
} from '../../../../constants/profile';
import {
  MAX_PREFERRED_STYLE_LENGTH,
  MAX_STYLE_FILTERS,
} from '../../../../constants/search';
import defaultUserAvatar from '../../../../images/user.svg';
import {
  buildProfilePreferredStyles,
  formatProfilePreferredStyles,
} from '../../../../utils/profilePreferredStyles';
import './index.scss';

function joinSeparators(parts) {
  return parts.filter(Boolean).map((part, i, arr) => (
    <React.Fragment key={i}>
      {part}
      {i < arr.length - 1 ? <span className="home-hero__sep" aria-hidden="true">/</span> : null}
    </React.Fragment>
  ));
}

function scrollToHashTarget(href, event) {
  if (typeof href !== 'string' || !href.startsWith('#')) return;
  const id = href.slice(1);
  const target = typeof document !== 'undefined' ? document.getElementById(id) : null;
  if (target && typeof target.scrollIntoView === 'function') {
    event?.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export default function HomeHero({
  id = 'info',
  username,
  handle,
  welcomeLine,
  tagline,
  avatarUrl,
  coverImageUrl,
  isOnline,
  presenceStatus,
  presenceLabel,
  joinedLabel,
  locationLabel,
  playsLabel,
  basedIn,
  preferredStyle,
  preferredStyles,
  mainInstrument,
  seekingLabel,
  seekingValue,
  seekingHref = '#seeking',
  onSeekingClick,
  editing = false,
  onFieldSave,
  displayNamePlaceholder = 'Preferred name',
  displayNameLabel = 'Preferred name',
  displayNameMaxLength = PROFILE_DISPLAY_NAME_MAX_LENGTH,
  taglinePlaceholder = 'Tell people what you’re about…',
  emptyPlaceholder = 'Not set',
  statsItems = [],
  onEditLocation,
  mainInstrumentHref = '#sound',
  onEditMainInstrument,
  onEditProfile,
  onShareProfile,
  onPublicView,
  visibility,
  onVisibilityChange,
  visibilityDisabled = false,
  onAvatarClick,
  onAvatarEdit,
  onCoverEdit,
  useDefaultAvatarFallback = false,
  extraActions,
  avatarPreviewLabel = 'View profile picture',
  avatarEditLabel = 'Edit',
  coverEditLabel = 'Edit cover',
  editLabel = 'Edit profile',
  shareLabel = 'Share profile',
  publicLabel = 'View as public',
  seekingHeading = 'Seeking',
  basedInLabel = 'Based in',
  preferredStyleLabel = 'Preferred style',
  preferredStyleEmptyLabel = PROFILE_PREFERRED_STYLE_EMPTY_LABEL,
  preferredStylePlaceholder = PROFILE_PREFERRED_STYLE_INPUT_PLACEHOLDER,
  preferredStyleClearLabel = 'Clear preferred style',
  mainInstrumentLabel = 'Main instrument',
  onlineLabel = 'Online now',
}) {
  const isBasedInEditable = editing && typeof onEditLocation === 'function';
  const isMainInstrumentEditable = editing;
  const titleRef = useRef(null);
  const [isTitleWrapped, setIsTitleWrapped] = useState(false);
  const preferredStyleEditorRef = useRef(null);
  const resolvedPreferredStyles = buildProfilePreferredStyles({ preferredStyle, preferredStyles });
  const preferredStyleDisplay = formatProfilePreferredStyles(resolvedPreferredStyles) || emptyPlaceholder;
  const hasPreferredStyles = resolvedPreferredStyles.length > 0;
  const avatarFallback = useDefaultAvatarFallback ? (
    <img src={defaultUserAvatar} alt={username || ''} />
  ) : (
    <span className="home-hero__avatar-placeholder">
      {(username || '?').slice(0, 1).toUpperCase()}
    </span>
  );
  const handleSeekingClick = (e) => {
    if (typeof onSeekingClick === 'function') {
      onSeekingClick(e);
      if (e.defaultPrevented) return;
    }
    scrollToHashTarget(seekingHref, e);
  };
  const handlePreferredStyleActivate = () => {
    preferredStyleEditorRef.current?.open?.();
  };
  const handleMainInstrumentClick = (e) => {
    if (typeof onEditMainInstrument === 'function') {
      onEditMainInstrument(e);
      if (e.defaultPrevented) return;
    }
    scrollToHashTarget(mainInstrumentHref, e);
  };
  const resolvedPresenceStatus = presenceStatus || (isOnline ? 'online' : null);
  const resolvedPresenceLabel = presenceLabel || (resolvedPresenceStatus === 'online' ? onlineLabel : null);
  const hasPresenceStatus = Boolean(resolvedPresenceStatus && resolvedPresenceLabel);
  const presenceClass = hasPresenceStatus
    ? `home-hero__presence--${resolvedPresenceStatus}`
    : null;
  const avatarPresenceClass = hasPresenceStatus
    ? `home-hero__avatar-status--${resolvedPresenceStatus}`
    : null;

  const topItems = joinSeparators([
    hasPresenceStatus ? (
      <span className={classNames('home-hero__presence', presenceClass)}>
        {resolvedPresenceStatus === 'online' ? (
          <LiveDot color="online" size="sm" />
        ) : (
          <span className="home-hero__presence-dot" aria-hidden="true" />
        )}
        <span>{resolvedPresenceLabel}</span>
      </span>
    ) : null,
    joinedLabel,
    locationLabel,
    playsLabel,
  ]);

  useLayoutEffect(() => {
    const title = titleRef.current;
    if (!title || typeof window === 'undefined') return undefined;

    const measureTitle = () => {
      const styles = window.getComputedStyle(title);
      const fontSize = Number.parseFloat(styles.fontSize);
      const parsedLineHeight = Number.parseFloat(styles.lineHeight);
      const lineHeight = Number.isFinite(parsedLineHeight)
        ? parsedLineHeight
        : fontSize * 0.86;
      const measuredHeight = title.getBoundingClientRect().height || title.scrollHeight || 0;
      const nextWrapped = Number.isFinite(lineHeight) && measuredHeight > lineHeight * 1.35;
      setIsTitleWrapped(nextWrapped);
    };

    measureTitle();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measureTitle);
      observer.observe(title);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', measureTitle);
    return () => window.removeEventListener('resize', measureTitle);
  }, [username]);

  return (
    <section className={classNames('home-hero', editing && 'home-hero--editing')} id={id}>
      <div className="home-hero__bg" />
      <div className={classNames('home-hero__photo', {
        'home-hero__photo--image': Boolean(coverImageUrl),
      })}
      >
        {coverImageUrl ? (
          <img src={coverImageUrl} alt="" aria-hidden="true" />
        ) : null}
      </div>
      <div className="home-hero__grain" />

      <HeroCoverEditButton
        className="home-hero__cover-edit"
        label={coverEditLabel}
        onClick={onCoverEdit}
      />

      <div className="home-hero__container">
        {topItems.length > 0 ? (
          <div className="home-hero__top">{topItems}</div>
        ) : null}

        {welcomeLine ? (
          <p className="home-hero__welcome">{welcomeLine}</p>
        ) : null}

        <div
          className={classNames(
            'home-hero__identity',
            isTitleWrapped && 'home-hero__identity--title-wrapped',
          )}
        >
          <div
            className={classNames('home-hero__avatar', {
              'home-hero__avatar--interactive': Boolean(onAvatarClick || onAvatarEdit),
              'home-hero__avatar--editing': editing && Boolean(onAvatarEdit),
            })}
          >
            {onAvatarClick ? (
              <button
                type="button"
                className="home-hero__avatar-trigger"
                onClick={onAvatarClick}
                aria-label={avatarPreviewLabel}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt={username || ''} />
                ) : (
                  avatarFallback
                )}
              </button>
            ) : (
              avatarUrl ? (
                <img src={avatarUrl} alt={username || ''} />
              ) : (
                avatarFallback
              )
            )}
            {onAvatarEdit ? (
              <button
                type="button"
                className="home-hero__avatar-edit"
                onClick={onAvatarEdit}
                aria-label={avatarEditLabel}
              >
                <PencilIcon width="14" height="14" />
                <span>{avatarEditLabel}</span>
              </button>
            ) : null}
            {hasPresenceStatus ? (
              <span
                className={classNames('home-hero__avatar-status', avatarPresenceClass)}
                aria-label={resolvedPresenceLabel}
                title={resolvedPresenceLabel}
              />
            ) : null}
          </div>
          <div className="home-hero__identity-text">
            <h1 className="home-hero__title" ref={titleRef}>
              {editing ? (
                <EditableText
                  editing
                  value={typeof username === 'string' ? username : ''}
                  placeholder={displayNamePlaceholder}
                  ariaLabel={displayNameLabel}
                  maxLength={displayNameMaxLength}
                  nativeMaxLength={false}
                  inputClassName="home-hero__title-input"
                  onSave={(next) => onFieldSave?.('displayName', next)}
                />
              ) : (
                <span>{username || ''}</span>
              )}
            </h1>
          </div>
          {handle ? <div className="home-hero__handle">{handle}</div> : null}
        </div>

        {editing ? (
          <p className="home-hero__tagline home-hero__tagline--editable">
            <EditableText
              editing
              multiline
              autoResize
              rows={3}
              value={typeof tagline === 'string' ? tagline : ''}
              placeholder={taglinePlaceholder}
              ariaLabel="About you"
              maxLength={1000}
              onSave={(next) => onFieldSave?.('about', next)}
            />
          </p>
        ) : (tagline ? (
          <p className="home-hero__tagline">{tagline}</p>
        ) : null)}

        {seekingValue ? (
          <a className="home-hero__seeking" href={seekingHref} onClick={handleSeekingClick}>
            <span className="home-hero__seeking-label">{seekingHeading}</span>
            <span>{seekingValue}</span>
            <span className="home-hero__seeking-icon">
              <ArrowRightIcon width="16" height="16" />
            </span>
          </a>
        ) : null}

        <div className="home-hero__meta">
          <HeroMetaField
            icon={<LocationIcon width="17" height="17" />}
            label={basedInLabel}
            value={basedIn || emptyPlaceholder}
            empty={!basedIn}
            editable={isBasedInEditable}
            onClick={onEditLocation}
          />
          <HeroMetaField
            icon={<StyleIcon width="17" height="17" />}
            label={preferredStyleLabel}
            value={preferredStyleDisplay}
            empty={!hasPreferredStyles}
            editable={editing}
            onClick={handlePreferredStyleActivate}
            wide={editing}
            className="home-hero__meta-item--preferred-style"
          >
            {editing ? (
              <StyleTagsEditor
                ref={preferredStyleEditorRef}
                values={resolvedPreferredStyles}
                placeholder={preferredStylePlaceholder}
                emptyLabel={preferredStyleEmptyLabel}
                ariaLabel={preferredStyleLabel}
                removeLabel={preferredStyleClearLabel}
                maxItems={MAX_STYLE_FILTERS}
                maxLength={MAX_PREFERRED_STYLE_LENGTH}
                menuAnchor="control"
                onSave={(next) => onFieldSave?.('preferredStyles', next)}
              />
            ) : null}
          </HeroMetaField>
          <HeroMetaField
            icon={<LyreIcon width="17" height="17" />}
            label={mainInstrumentLabel}
            value={mainInstrument || emptyPlaceholder}
            empty={!mainInstrument}
            editable={isMainInstrumentEditable}
            onClick={handleMainInstrumentClick}
          />
        </div>

        <div className="home-hero__actions">
          {extraActions}
          {onEditProfile ? (
            <button type="button" className="home-hero__btn home-hero__btn--accent" onClick={onEditProfile}>
              <PencilIcon width="16" height="16" />
              {editLabel}
            </button>
          ) : null}
          {onVisibilityChange ? (
            <PostVisibilityControl
              value={visibility || 'PUBLIC'}
              onChange={onVisibilityChange}
              disabled={visibilityDisabled}
              size="md"
              className="home-hero__visibility-control"
              options={PROFILE_VISIBILITY_OPTIONS}
            />
          ) : null}
          {onPublicView ? (
            <button type="button" className="home-hero__btn home-hero__btn--ghost" onClick={onPublicView}>
              <EyeIcon width="16" height="16" />
              {publicLabel}
            </button>
          ) : null}
          {onShareProfile ? (
            <button type="button" className="home-hero__btn home-hero__btn--ghost" onClick={onShareProfile}>
              <ShareIcon width="16" height="16" />
              {shareLabel}
            </button>
          ) : null}
        </div>
      </div>

      <EditorialHeroStatsRail stats={statsItems} namespace="home-hero" />
    </section>
  );
}

function HeroMetaField({
  icon,
  label,
  value,
  empty = false,
  editable = false,
  wide = false,
  onClick,
  className,
  children,
}) {
  const classes = classNames(
    'home-hero__meta-item',
    editable && 'home-hero__meta-item--editable',
    wide && 'home-hero__meta-item--wide',
    className,
  );
  const valueNode = children || (
    <span className={classNames('home-hero__meta-value', empty && 'home-hero__meta-value--empty')}>
      {value}
    </span>
  );
  const content = (
    <>
      <span className="home-hero__meta-label">
        {icon ? <span className="home-hero__meta-icon" aria-hidden="true">{icon}</span> : null}
        <span>{label}</span>
      </span>
      {valueNode}
    </>
  );

  if (editable && !children) {
    return (
      <button type="button" className={classes} onClick={onClick}>
        {content}
      </button>
    );
  }

  return <div className={classes} onClick={editable ? onClick : undefined}>{content}</div>;
}

HeroMetaField.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  empty: PropTypes.bool,
  editable: PropTypes.bool,
  wide: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

HomeHero.propTypes = {
  id: PropTypes.string,
  username: PropTypes.node,
  handle: PropTypes.node,
  welcomeLine: PropTypes.node,
  tagline: PropTypes.node,
  avatarUrl: PropTypes.string,
  coverImageUrl: PropTypes.string,
  isOnline: PropTypes.bool,
  presenceStatus: PropTypes.oneOf(['online', 'offline', 'away', 'unknown']),
  presenceLabel: PropTypes.string,
  joinedLabel: PropTypes.node,
  locationLabel: PropTypes.node,
  playsLabel: PropTypes.node,
  basedIn: PropTypes.node,
  preferredStyle: PropTypes.node,
  preferredStyles: PropTypes.arrayOf(PropTypes.string),
  mainInstrument: PropTypes.node,
  seekingLabel: PropTypes.node,
  seekingValue: PropTypes.node,
  seekingHref: PropTypes.string,
  onSeekingClick: PropTypes.func,
  editing: PropTypes.bool,
  onFieldSave: PropTypes.func,
  displayNamePlaceholder: PropTypes.string,
  displayNameLabel: PropTypes.string,
  displayNameMaxLength: PropTypes.number,
  taglinePlaceholder: PropTypes.string,
  emptyPlaceholder: PropTypes.string,
  statsItems: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.node,
    label: PropTypes.node,
  })),
  onEditProfile: PropTypes.func,
  onShareProfile: PropTypes.func,
  onPublicView: PropTypes.func,
  visibility: PropTypes.string,
  onVisibilityChange: PropTypes.func,
  visibilityDisabled: PropTypes.bool,
  onAvatarClick: PropTypes.func,
  onAvatarEdit: PropTypes.func,
  onCoverEdit: PropTypes.func,
  useDefaultAvatarFallback: PropTypes.bool,
  onEditLocation: PropTypes.func,
  mainInstrumentHref: PropTypes.string,
  onEditMainInstrument: PropTypes.func,
  extraActions: PropTypes.node,
  avatarPreviewLabel: PropTypes.string,
  avatarEditLabel: PropTypes.string,
  coverEditLabel: PropTypes.string,
  editLabel: PropTypes.string,
  shareLabel: PropTypes.string,
  publicLabel: PropTypes.string,
  seekingHeading: PropTypes.string,
  basedInLabel: PropTypes.string,
  preferredStyleLabel: PropTypes.string,
  preferredStyleEmptyLabel: PropTypes.string,
  preferredStylePlaceholder: PropTypes.string,
  preferredStyleClearLabel: PropTypes.string,
  mainInstrumentLabel: PropTypes.string,
  onlineLabel: PropTypes.string,
};
